import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type Subscription } from '../api';
import { useAuth } from '../auth';
import { ModalOverlay } from './ui';
import { EarlyPhysioOfferDialog } from './EarlyPhysioOfferDialog';
import { FreeSignupDialog } from './FreeSignupDialog';
import {
  approvedWelcomeHoldsOffer,
  subscribeApprovedWelcomeHold,
} from '../lib/studentWelcome';

const OFFER_COOKIE = 'pe_early_30_prompt';
const SIGNUP_COOKIE = 'pe_free_signup_prompt';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const OFFER_DELAY_MS = 30_000;

function cookieSeen(name: string): boolean {
  try {
    return document.cookie.split(';').some(part => part.trim().startsWith(`${name}=`));
  } catch {
    return false;
  }
}

function markCookieSeen(name: string) {
  try {
    document.cookie = `${name}=1; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

export function earlyPhysio30PromptShouldOpen() {
  return !cookieSeen(OFFER_COOKIE);
}

function signupPromptShouldOpen() {
  return !cookieSeen(SIGNUP_COOKIE);
}

function pathBlocksOffer(pathname: string) {
  return (
    pathname.startsWith('/admin')
    || pathname.startsWith('/clinic')
    || pathname.startsWith('/doctor')
    || pathname.startsWith('/student/checkout')
    || pathname.startsWith('/payment')
    || pathname.startsWith('/clinics')
    || pathname.startsWith('/doctors')
    || pathname.startsWith('/register')
    || pathname.startsWith('/login')
  );
}

function pathBlocksSignup(pathname: string) {
  return (
    pathBlocksOffer(pathname)
    || pathname.startsWith('/forgot-password')
    || pathname.startsWith('/reset-password')
  );
}

export function EarlyPhysio30Host() {
  const [waited, setWaited] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [holdTick, setHoldTick] = useState(0);
  const { me, loading } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => subscribeApprovedWelcomeHold(() => setHoldTick(n => n + 1)), []);

  useEffect(() => {
    const id = window.setTimeout(() => setWaited(true), OFFER_DELAY_MS);
    return () => window.clearTimeout(id);
  }, []);

  const holdForWelcome = approvedWelcomeHoldsOffer(me);

  const subQ = useQuery({
    queryKey: ['student-sub'],
    queryFn: () => api.get<{ subscription: Subscription | null }>('/api/student/subscription'),
    enabled: me?.role === 'student',
    staleTime: 30_000,
    retry: false,
  });

  const subscribed = subQ.data?.subscription?.status === 'active'
    || subQ.data?.subscription?.status === 'frozen';
  const roleOk = !me || me.role === 'student';
  const guest = !me;

  const canShowSignup = !loading
    && guest
    && !pathBlocksSignup(pathname)
    && signupPromptShouldOpen();

  const canShowOffer = waited
    && !signupOpen
    && !holdForWelcome
    && !loading
    && roleOk
    && !subscribed
    && !pathBlocksOffer(pathname)
    && earlyPhysio30PromptShouldOpen();

  useEffect(() => {
    if (canShowSignup) setSignupOpen(true);
  }, [canShowSignup]);

  useEffect(() => {
    if (canShowOffer) setOfferOpen(true);
  }, [canShowOffer, holdTick]);

  const closeSignup = () => {
    markCookieSeen(SIGNUP_COOKIE);
    setSignupOpen(false);
  };

  const closeOffer = () => {
    markCookieSeen(OFFER_COOKIE);
    setOfferOpen(false);
  };

  if (signupOpen) {
    return (
      <ModalOverlay>
        <FreeSignupDialog
          onRegister={() => {
            closeSignup();
            navigate('/register');
          }}
          onDismiss={closeSignup}
        />
      </ModalOverlay>
    );
  }

  if (!offerOpen) return null;

  return (
    <ModalOverlay>
      <EarlyPhysioOfferDialog
        onBuy={() => {
          closeOffer();
          navigate('/plans');
        }}
        onRegister={guest ? () => {
          closeOffer();
          navigate('/register');
        } : undefined}
        onDismiss={closeOffer}
        dismissLabel="I’ll check later"
      />
    </ModalOverlay>
  );
}
